import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { bulkUploadVendors } from "../../api/vendorApi";

const categories = ["supplier", "service_provider", "contractor"];
const vendorTypes = ["company", "individual"];

const initialVendor = {
  name: "",
  vendorType: "company",
  email: "",
  phone: "",
  contactPerson: "",
  category: "supplier",
  accountName: "",
  accountNumber: "",
  bankName: "",
  bankCode: ""
};

const toCamelCase = (str) => {
  return str
    .replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) =>
      chr ? chr.toUpperCase() : ""
    )
    .replace(/^./, (str) => str.toLowerCase());
};

export default function BulkVendorUploadModal({ closeModal, onVendorsUploaded }) {
  const [items, setItems] = useState([initialVendor]);
  const [loading, setLoading] = useState(false);

  const processImportedData = (data) => {
    if (!data || data.length === 0) {
      setItems([initialVendor]);
      toast.error("The file is empty or has no data.");
      return;
    }

    const newItems = data.map((item) => {
      let itemObject = { ...initialVendor };
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          const normalizedKey = toCamelCase(key.trim());
          if (itemObject.hasOwnProperty(normalizedKey)) {
            itemObject[normalizedKey] = item[key];
          }
        }
      }
      return itemObject;
    });

    const filteredItems = newItems.filter((r) => r.name && r.email);
    if (filteredItems.length > 0) {
      setItems(filteredItems);
      toast.success("File imported successfully. Review before saving.");
    } else {
      setItems([initialVendor]);
      toast.error("The file contains no valid vendor data. Ensure 'Name' and 'Email' are present.");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "csv") {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => processImportedData(results.data),
          error: () => toast.error("Failed to parse CSV file."),
        });
      } else if (ext === "xlsx") {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
            processImportedData(jsonData);
          } catch {
            toast.error("Failed to parse Excel file.");
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        toast.error("Unsupported file format. Upload .csv or .xlsx.");
      }
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Name",
      "Vendor Type",
      "Email",
      "Phone",
      "Contact Person",
      "Category",
      "Account Name",
      "Account Number",
      "Bank Name",
      "Bank Code"
    ];
    const csvContent = headers.join(",");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "vendor_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const itemsToSubmit = items.filter((r) => r.name && r.email).map(item => ({
      name: item.name,
      vendorType: item.vendorType?.toLowerCase() || 'company',
      email: item.email,
      phone: item.phone,
      contactPerson: item.contactPerson,
      category: item.category?.toLowerCase() || 'supplier',
      bankDetails: {
        accountName: item.accountName,
        accountNumber: item.accountNumber,
        bankName: item.bankName,
        bankCode: item.bankCode
      }
    }));

    if (itemsToSubmit.length === 0) {
      return toast.error("No valid vendors to add. Fill in at least Name and Email.");
    }

    setLoading(true);
    try {
      await bulkUploadVendors(itemsToSubmit);
      toast.success(`${itemsToSubmit.length} vendor(s) added successfully! 🎉`);
      onVendorsUploaded();
      closeModal();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Unexpected error.";
      toast.error(`Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (field, value, onChange) => {
    if (field === "category") {
      return (
        <select value={value} onChange={onChange} className="p-2 border border-gray-200 rounded-xl w-full text-sm focus:ring-2 focus:ring-orange-500 outline-none">
          <option value="">-- Select --</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }
    if (field === "vendorType") {
      return (
        <select value={value} onChange={onChange} className="p-2 border border-gray-200 rounded-xl w-full text-sm focus:ring-2 focus:ring-orange-500 outline-none min-w-[120px]">
          <option value="">-- Select --</option>
          {vendorTypes.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="p-2 border border-gray-200 rounded-xl w-full text-sm min-w-[150px] focus:ring-2 focus:ring-orange-500 outline-none"
      />
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex justify-center items-center z-[100] overflow-y-auto p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-6xl relative space-y-6 max-h-[90vh] overflow-y-auto border border-gray-100"
      >
        <button
          type="button"
          onClick={closeModal}
          className="absolute top-6 right-6 text-gray-400 hover:text-red-500 transition-colors p-2 font-black"
        >
          &times;
        </button>
        <h2 className="text-xl md:text-2xl font-black text-gray-800 uppercase tracking-widest">
          Bulk Onboard <span className="text-orange-600">Vendors</span>
        </h2>

        {/* Upload + Download Template */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-b border-gray-100 pb-6">
          <input
            type="file"
            accept=".csv, .xlsx"
            onChange={handleFileUpload}
            className="block w-full text-[10px] uppercase font-black tracking-widest text-gray-500 file:mr-6 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:bg-gray-100 file:text-gray-700 hover:file:bg-orange-50 hover:file:text-orange-600 transition-colors cursor-pointer"
          />
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="px-6 py-3 bg-gray-50 text-orange-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-100 transition-colors"
          >
            Download Template
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border border-gray-100 rounded-2xl shadow-sm">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/80">
              <tr>
                {[
                  "Name",
                  "Vendor Type",
                  "Email",
                  "Phone",
                  "Contact Person",
                  "Category",
                  "Account Name",
                  "Account Number",
                  "Bank Name",
                  "Bank Code"
                ].map((h, i) => (
                  <th key={i} className="p-5 text-left text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {items.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50/50">
                  {Object.keys(initialVendor).map((field, i) => (
                    <td key={i} className="p-2">
                      {renderInput(field, item[field], (e) => {
                        const newItems = [...items];
                        newItems[idx] = { ...newItems[idx], [field]: e.target.value };
                        setItems(newItems);
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={closeModal}
            className="px-6 py-4 border border-gray-200 text-gray-500 rounded-xl hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-colors active:scale-95"
            disabled={loading}
          >
            Abort Process
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-4 bg-gray-900 text-white rounded-xl hover:bg-orange-600 shadow-xl shadow-gray-200 text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Transmitting..." : "Initialize Import"}
          </button>
        </div>
      </form>
    </div>
  );
}
